import { environment } from '@env/environment';
import { EntityMap } from '@shared/models/types';

export const BASE = environment.haulmatic.baseUrl;
// const ZUUL = `${BASE}/api-gateway`;
export const ZUUL = environment.haulmatic.apiGateway;
const VERSION = 'v2';
const SCHEDULE = `${ZUUL}/schedule/api`;
const AUTH = `${ZUUL}/auth`;
// Public gateway surface registered by WebhookApi.java (ms-api-gateway), rewritten onto
// ms-webhook's internal /api/v1/** routes. Gated by JwtValidationFilter (plain staff bearer
// token, same as every other webapp route) - see WebhookService.orgHeaders() for why callers
// must supply X-Organization-ID themselves here.
const WEBHOOK = `${ZUUL}/api/external/v1/webhooks`;
const JOB_FILE = `${ZUUL}/api/jobfile`;
const SERVICE_REQUESTS = `${JOB_FILE}/service-requests`;
const SPOT_RFQS = `${JOB_FILE}/spot-rfqs`;
const FLEET_MANAGER = `${ZUUL}/api/org/fleet-manager`; // TODO: change to correct path // TODO: change to correct path
const CONTROL_TOWER = `${ZUUL}/control-tower`; // TODO change using Navigation - Schedule and JPM in same Application
const JPM = `${ZUUL}/api/jpm`;
const S3 = `${ZUUL}/api/jpm/s3`;
const YOU_ARE_HERE = `${ZUUL}/api/pdf`;
const GATEWAY = `${ZUUL}/api/gateway`;
const QUOTATION = `${ZUUL}/api/fleetmanager/quotation`;
const RATE_CARD = `${ZUUL}/api/ratesheet`;
const SMART_CONTRACT = `${ZUUL}/api/smartcontract`;
const FRONTEND_V4 = BASE;
const FRONTEND_MASTER_DATA_V8 = `${BASE}/job-master`;
const FM_GPS = `${ZUUL}/api/telematics`;
const FILE_HANDLER = `${ZUUL}/api/file-handler`;
const WORKFLOW = `${ZUUL}/api/dynamicworkflow`;
const MASTER_DATA = `${ZUUL}/api/masterdata`;
const INVOICE = `${ZUUL}/api/invoice`;
const LOCATIONS = `${ZUUL}/api/location`;
const ORGANIZATION = `${ZUUL}/api/organization-management`;
const REPORT = `${ZUUL}/api/reports`;
const API_MANAGEMENT = `${ZUUL}/api-management`;
const API_GATEWAY = `${ZUUL}`;
const CORTEX = `${ZUUL}/api/cortex`;
const PLANNING_AGENT = `${CORTEX}/agent/planning`;
const CONTROL_TOWER_BRAIN = `${CORTEX}/agent/control-tower-brain`;
export const GRAPHQL = `${ZUUL}/api/graphql`;
export const INVOICE_GRAPHQL = `${ZUUL}/api/invoice`;
export const REPORT_GRAPHQL = `${ZUUL}/api/report`;
// export const GRAPHQL_COMMON = `${ZUUL}/api/graphql-common`;
export const GRAPHQL_COMMON = `${ZUUL}/api/graphql-common`;
export const FILE_CONVERTER = `${ZUUL}/api/file-converter`;
const AUDIT_LOGGING = `${ZUUL}/api/audit-logging`;
export const NOTIFICATION = `${ZUUL}/api/v1/notifications`;

type StringFormatter = (
  urlParams: string[],
  queryParams?: EntityMap<string, string | number | boolean>,
) => string;
export const formatter = (apiString: string): { format: StringFormatter } => {
  const format = (urlParams: string[]): string => {
    // process url params
    const url = urlParams.reduce(
      (prev: string, param: string, index: number) => {
        prev = prev.replace(`{${index}}`, param);
        return prev;
      },
      apiString,
    );
    // TODO: process query params map

    return url;
  };
  return { format };
};
export const API = {
  auth: {
    getToken: `${AUTH}/users/login`,
    refreshToken: `${AUTH}/users/refresh`,
    getUserByUserId: formatter(`${AUTH}/identity/{0}`),
    getTenants: `${AUTH}/tenant-details`,
    resetPasswordInit: `${AUTH}/users/reset-password/init`,
    resetPassword: `${AUTH}/users/reset-password/reset`,
    generateServiceAccount: `${AUTH}/service-accounts/generate`,
    logout: `${AUTH}/users/logout`,
    verifyMfaLogin: `${AUTH}/users/verify-mfa-login`,
    sendLoginMfaOtp: `${AUTH}/users/send-login-mfa-otp`,
    completeMfaSetupLogin: `${AUTH}/users/complete-mfa-setup-login`,
    validateAccountActivationToken: `${AUTH}/account-activation/validate`,
    setPasswordWithActivationToken: `${AUTH}/account-activation/set-password`,
  },
  webhook: {
    endpoints: `${WEBHOOK}/endpoints`,
    endpointById: formatter(`${WEBHOOK}/endpoints/{0}`),
    subscriptions: `${WEBHOOK}/subscriptions`,
    subscriptionById: formatter(`${WEBHOOK}/subscriptions/{0}`),
    deliveries: `${WEBHOOK}/deliveries`,
    deliveryById: formatter(`${WEBHOOK}/deliveries/{0}`),
  },
  mfa: {
    initSetup: `${AUTH}/mfa/init-setup`,
    verifyEnable: `${AUTH}/mfa/verify-enable`,
    initEmailSetup: `${AUTH}/mfa/init-email-setup`,
    verifyEmailCode: `${AUTH}/mfa/verify-email-code`,
    initSmsSetup: `${AUTH}/mfa/init-sms-setup`,
    verifySmsCode: `${AUTH}/mfa/verify-sms-code`,
    resendOtp: `${AUTH}/mfa/resend-otp`,
    disable: `${AUTH}/mfa/disable`,
    regenerateBackupCodes: `${AUTH}/mfa/regenerate-backup-codes`,
    status: (userId: string) => `${AUTH}/mfa/status/${userId}`,
  },
  schedule: {
    getContainerSchedules: formatter(`${SCHEDULE}/container/${VERSION}/{0}`),
    getContainerSchedulesV2: formatter(
      `${SCHEDULE}/container/v2/org/{0}/jobs/{1}/internal-assignable-loads`,
    ),
    getContainerSchedulesV2ByTenant: formatter(
      `${SCHEDULE}/container/v2/tenant/{0}/org/{1}/job/{2}/internal-assignable-loads`,
    ),
    getContainerSchedule: formatter(`${SCHEDULE}/container/${VERSION}/{0}/{1}`),
    saveResourceUpdates: `${SCHEDULE}/container/${VERSION}/saveScheduleResources`,
    saveResourceUpdatesBE: formatter(
      `${SCHEDULE}/container/${VERSION}/save-resource-assignment-be/tenant/{0}`,
    ),
    bulkSaveResourceAssignmentBE: formatter(
      `${SCHEDULE}/container/${VERSION}/bulk-save-resource-assignment-be/tenant/{0}`,
    ),
    getResources: `${SCHEDULE}/resources/${VERSION}/findAvailableOrganizationResources/`,
    getResourcesPaginated: `${SCHEDULE}/resources/${VERSION}/findAvailableOrganizationResourcesPaginated/`,
    getResourcesPaginatedForSharedLoad: formatter(
      `${SCHEDULE}/resources/${VERSION}/org/{0}/load-type/{1}/findAvailableOrganizationResourcesPaginated`,
    ),
    getResourceToContainerSchedules: `${SCHEDULE}/resources/${VERSION}/resourceToContainerSchedule`,
    getMostAppropriateResourceToContainerSchedules: `${SCHEDULE}/resources/${VERSION}/load-resource-grid`,
    notify: `${SCHEDULE}/container/${VERSION}/notify`,
    unAssign: `${SCHEDULE}/container/v2/un-assign`,
    unassignResourcesForContainer: formatter(
      `${SCHEDULE}/unassign-resources/tenant/{0}/orgId/{1}/jobRefId/{2}/containerId/{3}`,
    ),
    splitAll: `${SCHEDULE}/container/${VERSION}/splitAll`,
    getExternalVehicles: `${SCHEDULE}/resources/${VERSION}/load-external-vehicles`,
    autoAssignContainer: formatter(
      `${SCHEDULE}/container/${VERSION}/auto-re-assign-resources/{0}/{1}/{2}?legOrder={3}`,
    ),
    updateLegsDates: formatter(
      `${SCHEDULE}/container/${VERSION}/orgId/{0}/jobRefId/{1}/containerId/{2}/legs/dates`,
    ),
    saveScheduleResourcesBe: formatter(
      `${SCHEDULE}/container/${VERSION}/save-schedule-resources-be/org/{0}/job/{1}`,
    ),
    reScheduleCheck: `${SCHEDULE}/resources/${VERSION}/re-schedule/availability-check`,
    findResourceViewLogsByLeg: formatter(
      `${SCHEDULE}/resources/${VERSION}/resources-history/job/{0}/container/{1}/leg/{2}`,
    ),
    saveResourcePool: `${SCHEDULE}/resource-pool/`,
    getResourcePoolByJobRefIdAndContainerId: formatter(
      `${SCHEDULE}/resource-pool/org/{0}/job/{1}/container/{2}`,
    ),
    initResourcePool: formatter(
      `${SCHEDULE}/resource-pool/init-pool/org/{0}/job/{1}`,
    ),
    removePool: formatter(`${SCHEDULE}/resource-pool/org/{0}/job/{1}/pool/{2}`),
    getPoolsByJobRefId: formatter(`${SCHEDULE}/resource-pool/org/{0}/job/{1}`),
    getAvailableContainers: formatter(
      `${SCHEDULE}/resource-pool/available-containers/org/{0}/job/{1}`,
    ),
    addContainersToPool: `${SCHEDULE}/resource-pool/addContainersToPool`,
    removeContainerFromPool: formatter(
      `${SCHEDULE}/resource-pool/org/{0}/job/{1}/container/{2}`,
    ),
    addTeamToPool: `${SCHEDULE}/resource-pool/saveTeamsToPool`,
    removeTeam: formatter(
      `${SCHEDULE}/resource-pool/org/{0}/job/{1}/pool/{2}/team/{3}`,
    ),
    notifyPool: formatter(
      `${SCHEDULE}/resource-pool/notify-pool/org/{0}/job/{1}/pool/{2}`,
    ),
    removeAllContainer: formatter(
      `${SCHEDULE}/resource-pool/org/{0}/job/{1}/pool/{2}/container/all`,
    ),
    getPoolContainerCount: formatter(
      `${SCHEDULE}/resource-pool/container-count/org/{0}/job/{1}`,
    ),
    selectAllContainers: `${SCHEDULE}/resource-pool/addContainersToPool/select-all`,
    getAllResourcePoolTemplates: formatter(
      `${SCHEDULE}/resource-pool/team-template/all/org/{0}`,
    ),
    addTeamsTemplate: `${SCHEDULE}/resource-pool/template/save-team-template`,
    getFuResourceDetails: formatter(
      `${SCHEDULE}/resources/v2/fleet-utilization/from/{0}/to/{1}/type/{2}`,
    ),
    getScheduleByJobRefIdAndContainerId: formatter(
      `${SCHEDULE}/schedule/{0}/{1}`,
    ),
    getScheduleByJobRefIdAndContainerIdForSpecificTenant: formatter(
      `${SCHEDULE}/schedule/{0}/{1}/by-tenant`,
    ),
  },
  jobFile: {
    createJobFile: `${JOB_FILE}/init-jobFile`,
    createJobFileV2: `${JOB_FILE}/v2/init-jobFile`,
    getJobFiles: `${JOB_FILE}/ready-to-schedule`,
    getJobFileSummaries: `${JOB_FILE}/ready-to-schedule-jobs`,
    filterJobFileSummaries: `${JOB_FILE}/ready-to-schedule-jobs/filter`,
    getJobFilesByJobGroupId: formatter(
      `${JOB_FILE}/job-files?pageNum={0}&pageSize={1}`,
    ),
    getAllJobFiles: formatter(`${JOB_FILE}/org/{0}/find`),
    filterJobFiles: `${JOB_FILE}/filter`,
    getJobFileByOrgIdAndJobRefId: formatter(`${JOB_FILE}/orgs/{0}/jobs/{1}`),
    getScheduledLoadIds: formatter(`${JOB_FILE}/org/{0}/jobs/{1}/scheduled-load-ids`),
    // Flags loads as scheduled, which is what makes them show up in the call above before they
    // have been scheduled.
    markContainersScheduled: formatter(`${JOB_FILE}/org/{0}/jobs/{1}/containers/internal-flag`),
    // Same job file trimmed to what the job-summary page renders. The endpoint above is left in
    // place so any other consumer keeps its full response.
    getJobFileForJobSummaryPage: formatter(
      `${JOB_FILE}/orgs/{0}/jobs/{1}/job-summary-page`,
    ),
    // Route polylines for one container. Kept out of the job-summary payload because they were
    // 96% of it while only the selected load's route is ever drawn.
    getContainerRoute: formatter(
      `${JOB_FILE}/orgs/{0}/jobs/{1}/containers/{2}/route`,
    ),
    // Leg route polylines by encodedPathId (body: up to 500 ids). See RoutePolylineService.
    getRoutePolylines: `${JOB_FILE}/route-polylines/batch`,
    getShipmentOrderSpecs: formatter(`${JOB_FILE}/org/{0}/jobs/{1}/shipment-order-specs`),
    getShipmentOrderSpecIds: formatter(`${JOB_FILE}/org/{0}/jobs/{1}/shipment-order-specs/ids`),
    getAllShipmentOrderSpecs: formatter(`${JOB_FILE}/org/{0}/jobs/{1}/shipment-order-specs/all`),
    getShipmentOrderSpecsPanel: formatter(`${JOB_FILE}/org/{0}/jobs/{1}/shipment-order-specs/panel`),
    assignAllShipmentOrdersToContainer: formatter(`${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/assign-all-shipment-orders`),
    getShipmentOrderSpecsPanelIds: formatter(`${JOB_FILE}/org/{0}/jobs/{1}/shipment-order-specs/panel/ids`),
    deleteVisibleShipmentOrderSpecs: formatter(`${JOB_FILE}/org/{0}/jobs/{1}/shipment-order-specs/delete-visible`),
    getJobFileByOrgIdAndLoadId: formatter(`${JOB_FILE}/org/{0}/by-load/{1}`),
    getJobFileWithLoadAssignmentDetailsByOrgIdAndJobRefId: formatter(
      `${JOB_FILE}/{0}/{1}/load-assignment-details`,
    ),
    // Same job file trimmed to what the load-assignment page renders. The endpoint above is
    // left in place so any other consumer keeps its full response.
    getJobFileForLoadAssignmentPage: formatter(
      `${JOB_FILE}/{0}/{1}/load-assignment-page`,
    ),
    getJobFileByJobRefId: formatter(`${JOB_FILE}/jobs/{0}`),
    getPreviousJobOrg: formatter(`${JOB_FILE}/jobs/{0}/previous-job-org`),
    updateGPSDetails: formatter(
      `${JOB_FILE}/jobs/{0}/container/{1}/update-gps-details`,
    ),
    getWorkOrdersDefinitionsByJpmIdAndLegId: formatter(
      `${JOB_FILE}/monitoring/v2/work-orders/definitions/jpm/{0}/leg/{1}/find`,
    ),
    getOptionalWorkOrdersDefinitions: formatter(
      `${JOB_FILE}/monitoring/v2/work-orders/definitions/activity/{0}/work-order/{1}?orgId={2}&jobRefId={3}`,
    ),
    addAdditionalWorkOrders: formatter(
      `${JOB_FILE}/monitoring/v2/work-orders/previous-work-order/{0}/add`,
    ),
    getWorkOrdersByJpmIdAndLegId: formatter(
      `${JOB_FILE}/monitoring/v2/work-orders/jpm/{0}/leg/{1}/find`,
    ),
    getWorkOrdersByJpmId: formatter(
      `${JOB_FILE}/monitoring/v2/work-orders/jpm/{0}/find`,
    ),
    findWorkOrdersByJpmIdAndWorkOrderIds: formatter(
      `${JOB_FILE}/monitoring/v2/work-orders/jpm/{0}/find-by-workorder/summary`,
    ),
    getWorkOrdersByWorkOrderId: formatter(
      `${JOB_FILE}/monitoring/v2//work-orders/work-order/{0}/find`,
    ),
    deleteWorkOrdersByWorkOrderId: formatter(
      `${JOB_FILE}/monitoring/v2/work-orders/work-order/{0}`,
    ),
    getWorkOrderMonitoringActivitiesByOrdIdAndJobRefIdAndContainerId: formatter(
      `${JOB_FILE}/monitoring/v2/activities/find/{0}/{1}/{2}`,
    ),
    getJpmCompletionPercentage: formatter(
      `${JOB_FILE}/monitoring/v2/jpm-completion-percentage/org/{0}/job/{1}`,
    ),
    saveSpecialInstructionOfActivity: formatter(
      `${JOB_FILE}/monitoring/v2/work-orders/{0}/special-instructions`,
    ),
    getActivityDocumentsForJpm: formatter(
      `${JOB_FILE}/monitoring/v2/activities/jpm/{0}/documents`,
    ),
    createInitContainer: formatter(`${JOB_FILE}/org/{0}/jobs/{1}/confirm`),
    saveInitContainer: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/save-containers`,
    ),
    findInitContainersByContainerIds: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/init-containers`,
    ),
    getContainer: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/add-container`,
    ),
    getContainerByJobRefIdAndContainerId: formatter(
      `${JOB_FILE}/jobs/{0}/containers/{1}`,
    ),
    removeLoadReferenceForContainer: formatter(
      `${JOB_FILE}/jobs/{0}/containers/{1}/load-reference`,
    ),
    getContainerByOrder: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/order/{2}/add-container`,
    ),
    activityList: `${JOB_FILE}/monitoring/v2/activities`,
    createActivity: `${JOB_FILE}/monitoring/v2/activities/activity/create`,
    moveActivity: `${JOB_FILE}/monitoring/v2/activities/activity/drag-drop`,
    createUnPlannedStopActivity: `${JOB_FILE}/monitoring/v2/activities/activity/unplanned-stop-to-activity`,
    removeActivity: formatter(
      `${JOB_FILE}/monitoring/v2/activities/activity/{0}/by/{1}`,
    ),
    modifyActivity: `${JOB_FILE}/monitoring/v2/activities/activity/modify`,
    modifyLegDistance: formatter(
      `${JOB_FILE}/monitoring/v2/activities/leg-distance/modify/jpm-id/{0}/leg-id/{1}`,
    ),
    modifyActivityBilling: formatter(
      `${JOB_FILE}/monitoring/v2/activities/{0}/set-billing`,
    ),
    getWorkOrderDefinition: formatter(
      `${JOB_FILE}/monitoring/v2/work-orders/definitions/find/{0}`,
    ),
    getWorkOrderDefinitionsOfAdditionalActivitiesByWorkFlowId: formatter(
      `${JOB_FILE}/monitoring/v2/work-orders/definitions/additional-services/workFlowId/{0}/find`,
    ),
    assignAndSchedule: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/order/{2}/assign-schedule`,
    ),
    generateInitLegs: `${JOB_FILE}/legs/generate/dry`,
    terminateJob: formatter(
      `${JOB_FILE}/terminatejobfile/orgId/{0}/jobRefId/{1}`,
    ),
    assignSupervisorToQuotation: `${JOB_FILE}/quotation/work-order/assign-supervisor-to-quotation`,
    assignSupervisorToGenerateQuotation: formatter(
      `${JOB_FILE}/quotation/work-order/org/{0}/jobs/{1}/order/{2}/supervisor/{3}/assign-supervisor-to-generate-quotation`,
    ),
    sendForApproval: formatter(
      `${JOB_FILE}/wizard/work-order/org/{0}/jobs/{1}/order/{2}/send-for-approval-container-run-time`,
    ),
    sendQuotationToFleetManager: formatter(
      `${JOB_FILE}/quotation/work-order/org/{0}/jobs/{1}/order/{2}/send-quotation-to-fleetManager`,
    ),
    getContainerTemplates: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/service-sequence-template/search?searchKey={2}`,
    ),
    getContainerTemplatesByOperation: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/operation/{2}/service-sequence-template/search?searchKey={3}`,
    ),
    removeTemplate: formatter(
      `${JOB_FILE}/service-sequence-template/template/{0}/remove`,
    ),
    contractedRate: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/order/{2}/apply-contracted-rate`,
    ),
    getWorkOrderDefinitionList: `${JOB_FILE}/monitoring/v2/work-orders/definitions/find`,
    saveServiceSequenceTemplate: `${JOB_FILE}/service-sequence-template`,
    updateServiceSequenceTemplate: `${JOB_FILE}/service-sequence-template`,
    downloadJobSummary: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/order/{2}/pdf/job-summary`,
    ),
    generateJobSummaryAsync: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/order/{2}/pdf/job-summary/async`,
    ),
    generateCollaboratorsAsync: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/order/{2}/collaborators/generate/async`,
    ),
    generateJobSummary: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/order/{2}/pdf/job-summary/generate`,
    ),
    generateContainerSchedulesAsync: formatter(
      `${JOB_FILE}/container-schedule/org/{0}/jobs/{1}/order/{2}/generate-async`,
    ),
    handlePendingOrders: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/handle-pending-orders`,
    ),
    getWorkOrderDefinitionListWithChildren: `${JOB_FILE}/monitoring/v2/work-orders/definitions/find/with-children`,
    applyServiceSequenceTemplate: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/sst/{3}/apply`,
    ),
    updateService: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/services/service/update`,
    ),
    addService: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/index/{3}/services/service/add`,
    ),
    batchSaveServices: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/services/service/batch-save`,
    ),
    stageServiceBatch: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/services/service/stage`,
    ),
    discardStagedServiceBatch: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/services/service/stage/{3}`,
    ),
    generateDryServiceByActivityId: formatter(
      `${JOB_FILE}/monitoring/v2/services/by-activity/{0}/billingActivated/{1}/billingIndicator/{2}/generate/dry?jobRefId={3}&containerId={4}`,
    ),
    removeService: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/service/{3}/remove`,
    ),
    duplicateContainer: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/duplicate`,
    ),
    saveContainerBulk: formatter(`${JOB_FILE}/org/{0}/jobs/{1}/order/{2}/bulk`),
    addNewContainer: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/container/add`,
    ),
    addNewContainerForBookingEngine: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/container/add/booking-engine`,
    ),
    removeContainer: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/remove`,
    ),
    removeAllContainers: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/order/{2}/containers/remove`,
    ),
    updateContainer: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/update`,
    ),
    updateContainerFcl: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/fcl/update`,
    ),
    addVehicleCategoryFieldsToContainer: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/add-vehicle-category-fields`,
    ),
    addVehicleDataToContainer: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/add-vehicle-data`,
    ),
    updateLoadKeyDetails: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/key-details`,
    ),
    updateOrdersData: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/shipment-orders`,
    ),
    moveToAnotherContainer: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/move-to-another-container`,
    ),
    getVehicleAccessibilityByOrderIds: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/verify-accessibility`,
    ),
    updateContainerByUseExpressWayOption: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/express-ways/{3}`,
    ),
    updateMapDistanceByUseExpressWayOption: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/express-ways/{3}/map-distance`,
    ),
    saveWoOperationalTypeAs: formatter(
      `${JOB_FILE}/monitoring/v2/work-orders/activities/wo-operational-type/save-as/{0}`,
    ),
    optimizeOrderMovedContainers: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/fromContainer/{2}/toContainer/{3}/optimize-containers`,
    ),
    optimizeContainers: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/optimize-routes`,
    ),
    saveContainerData: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/save-container`,
    ),
    updateContainerWhenAddWayPoint: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/update-container-add-wayPoint`,
    ),
    calculateRouteDetails: formatter(
      `${JOB_FILE}/locations/from/location/{0}/to/location/{1}/calculated-route`,
    ),
    retrieveCategorizedLocationNames: formatter(
      `${JOB_FILE}/workflow/org/{0}/jobs/{1}/locations/categorize`,
    ),
    getJobSummary: formatter(
      `${JOB_FILE}/org/{0}/job/{1}/order/{2}/job-summary/generate`,
    ),
    finalizeJobFile: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/order/{2}/generate-container-route-images`,
    ),
    getJobSummaryPdfPreSigned: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/job-summary/pdf/pre-signed`,
    ),
    gatConfig: formatter(`${JOB_FILE}/config/job-summary/{0}/{1}`),
    jobSendForApproval: formatter(
      `${JOB_FILE}/wizard/work-order/org/{0}/jobs/{1}/order/{2}/job-send-for-approval`,
    ),
    loadWorkflowDefinition: formatter(
      `${JOB_FILE}/workflow/definitions/definition/v2/{0}`,
    ),
    updateCustomsChannel: formatter(
      `${JOB_FILE}/org/{0}/job/{1}/container/{2}/customsChannel/{3}/update-customs-channel`,
    ),
    updateChannelAndRemoveService: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/service/{3}/channel/{4}/update-customs-channel-and-remove-service`,
    ),
    updateChannelAndAddDryService: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/index/{3}/activity/{4}/channel/{5}/update-customs-channel-and-add-dry-service?operationId={6}&workflowId={7}`,
    ),
    saveWizardRowData: formatter(`${JOB_FILE}/org/{0}/jobs/{1}`),
    saveWizardVehicleCapacities: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/vehicle-capacities`,
    ),
    saveWizardDistributionWorkflowData: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/distribution-workflow-data`,
    ),
    saveWizardDistributionWorkflowDataAsync: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/distribution-workflow-data/async`,
    ),
    // Paginated selector: persist the compact vehicle-selection intent (materialized server-side).
    saveVehicleSelectionIntent: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/vehicle-selection`,
    ),
    // Returns category names+counts for the UI AND persists the real fleet into the jobfile.
    initVehicleSelection: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/vehicle-selection/init`,
    ),
    // Patch only the jobfile's vehicle capacities (selector Add/Update writes through immediately).
    saveVehicleCapacities: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/vehicle-capacities`,
    ),
    // Patch ONLY the standard-vehicle bucket (internal/external left untouched).
    saveStandardVehicleCapacities: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/vehicle-capacities/standard`,
    ),
    // Paginated vehicle rows read FROM the jobfile (saved/edited data, not fresh org-manager data).
    getJobFileVehiclePage: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/vehicle-selection/page`,
    ),
    // Apply per-vehicle selector edits by id (page-safe: only the listed vehicles change).
    updateVehicleRows: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/vehicle-selection/vehicles`,
    ),
    // Set how many vehicles of a category are used (selects the first N, deselects the rest).
    setCategorySelectedCount: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/vehicle-selection/category-count`,
    ),
    // Selector categories split by ownership, from the jobfile (no vehicle data loaded).
    getVehicleSelectionCategories: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/vehicle-selection/categories`,
    ),
    // How many stored vehicles have an individually-edited utilization — asked before changing the
    // global, since the browser holds no vehicle data to work that out itself.
    getUtilizationImpact: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/vehicle-selection/utilization-impact`,
    ),
    // Apply the global capacity utilization to the jobfile's vehicles, leaving the selection alone.
    applyGlobalUtilization: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/vehicle-selection/global-utilization`,
    ),
    getOptimizationStatus: formatter(
      `${JOB_FILE}/optimization-status/{0}`,
    ),
    saveShipmentOrderSpecsToJobFile: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/shipment-order-specs-with-ids`,
    ),
    appendShipmentOrderSpecsToJobFile: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/shipment-order-specs-append`,
    ),
    assignOrdersToContainer: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/assign-orders`,
    ),
    assignShipmentOrdersToContainer: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/assign-shipment-orders`,
    ),
    removeOrderFromContainerActivities: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/orders/{3}`,
    ),
    downloadPackingList: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/shipment-orders/download`,
    ),
    downloadPackingListZip: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/shipment-orders/download`,
    ),
    getJobsForTaskFeed: formatter(
      `${JOB_FILE}/task-feed/org/{0}/assignedType/{1}/userId/{2}/state/{3}/module/{4}/pageNum/{5}/pageSize/{6}/searchKey/{7}`,
    ),
    getTaskFeedJobCount: formatter(
      `${JOB_FILE}/task-feed/org/{0}/userId/{1}/count`,
    ),
    getOngoingJobs: formatter(
      `${JOB_FILE}/task-feed/ongoingJobs/org/{0}/userId/{1}/pageNum/{2}/pageSize/{3}/searchKey/{4}`,
    ),
    getOngoingJobById: formatter(
      `${JOB_FILE}/task-feed/ongoingJobs/org/{0}/job/{1}`,
    ),
    getTaskFeedJobByJobRefId: formatter(
      `${JOB_FILE}/job-files/inprogress-workorders/org/{0}/job/{1}`,
    ),
    getActivityTypeWorkOrdersByFilteringWithDate: `${JOB_FILE}/monitoring/v2/work-orders/activity-type`,
    resetOptimizationResult: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/reset-optimization-result`,
    ),
    createMonitorsBE: formatter(
      `${JOB_FILE}/monitoring/v2/org/{0}/jobs/{1}/create-monitors-be`,
    ),
    // job groups
    createJobGroup: `${JOB_FILE}/job-groups`,
    getJobGroupsWithRelatedJobs: formatter(
      `${JOB_FILE}/job-groups/search/orgId/{0}/pageNum/{1}/pageSize/{2}`,
    ),
    getJobGroups: formatter(
      `${JOB_FILE}/job-groups/orgId/{0}/pageNum/{1}/pageSize/{2}`,
    ),
    deleteJobGroup: formatter(`${JOB_FILE}/job-groups/{0}`),
    uploadWizardAttachments: `${JOB_FILE}`,
    removeWizardAttachment: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/attachment/{2}/remove-document`,
    ),
    moveJobFileToGroup: `${JOB_FILE}/job-groups/move-job-to`,
    inviteCollaborators: `${JOB_FILE}/invite-collaborators`,
    removeCollaborator: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/remove-collaborator/{2}`,
    ),
    // collaborators
    getJobVerticalCollaboratorsOfContainer: formatter(
      `${JOB_FILE}/collaborators/org/{0}/jobs/{1}/container/{2}`,
    ),
    inviteCollaboratorsOfJob: `${JOB_FILE}/collaborators/assign/persons`,
    saveJobVerticalCollab: `${JOB_FILE}/collaborators/collaborator`,
    editCollaboratorLoads: `${JOB_FILE}/collaborators/person/loads`,
    deleteJobVerticalCollab: formatter(
      `${JOB_FILE}/collaborators/jobVertical/{0}/person/{1}`,
    ),
    deleteLoadOfCollab: formatter(
      `${JOB_FILE}/collaborators/jobVertical/{0}/jpm/{1}/container/{2}/person/{3}`,
    ),
    setBillingStartAndEnd: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/services/{3}/set-billing`,
    ),
    setLegFreezer: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/legs/{3}/set-freezer`,
    ),
    loadShipmentOrderSpecs: formatter(
      `${JOB_FILE}/shipment-order-spec/orgId/{0}/pageNum/{1}/pageSize/{2}`,
    ),
    filterShipmentOrderSpecs: `${JOB_FILE}/shipment-order-spec/filter`,
    uploadShipmentOrderExcel: formatter(
      `${JOB_FILE}/shipment-order-spec/org/{0}/import-excel`,
    ),
    uploadShipmentOrderExcelDomesticWithJob: formatter(
      `${JOB_FILE}/shipment-order-spec/org/{0}/jobs/{1}/import-excel-domestic`,
    ),
    checkDuplicateOrderIds: formatter(
      `${JOB_FILE}/shipment-order-spec/org/{0}/jobs/{1}/import-excel-orderId-check`,
    ),
    uploadShipmentOrderExcelToJob: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/distribution-workflow-data/shipment-order-specs-v2`,
    ),
    addShipmentOrdersToJob: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/add-shipment-orders`,
    ),
    mergeShipmentOrdersToJob: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/merge-shipment-orders`,
    ),
    checkShipmentOrderCanDelete: formatter(
      `${JOB_FILE}/shipment-order-spec/{0}/can-delete`,
    ),
    checkAllShipmentOrdersCanDelete: formatter(
      `${JOB_FILE}/shipment-order-spec/org/{0}/can-delete-all`,
    ),
    deleteShipmentOrder: formatter(`${JOB_FILE}/shipment-order-spec/{0}`),
    deleteShipmentOrdersByIds: `${JOB_FILE}/shipment-order-spec/batch-delete`,
    updateShipmentOrderPropertiesBatch: formatter(
      `${JOB_FILE}/shipment-order-spec/org/{0}/batch/properties`,
    ),
    deleteShipmentOrderFromJobFile: formatter(
      `${JOB_FILE}/{0}/shipmentOrderSpec/{1}/remove-shipment-order-spec`,
    ),
    deleteShipmentOrderFromJobFileWithParam: formatter(
      `${JOB_FILE}/{0}/shipmentOrderSpec/{1}/delete-shipment-order-spec`,
    ),
    deleteBatchShipmentOrdersFromJobFile: formatter(
      `${JOB_FILE}/{0}/shipmentOrderSpec/delete/batch`,
    ),
    deleteAllShipmentOrdersFromJobFile: formatter(
      `${JOB_FILE}/{0}/shipmentOrderSpec/remove/all`,
    ),
    checkOrderDeletionImpact: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/orders/{3}/deletion-impact`,
    ),
    confirmOrderDeletion: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/orders/confirm-deletion`,
    ),
    checkBatchOrderDeletionImpact: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/orders/batch-deletion-impact`,
    ),
    confirmBatchOrderDeletion: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/orders/batch-confirm-deletion`,
    ),
    checkMultiContainerOrderDeletionImpact: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/orders/multi-container-deletion-impact`,
    ),
    confirmMultiContainerOrderDeletion: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/orders/multi-container-confirm-deletion`,
    ),
    deleteOrderFromContainer: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/orders/{3}/remove`,
    ),
    deleteAllShipmentOrdersFromJobFileWithParam: formatter(
      `${JOB_FILE}/{0}/shipmentOrderSpec/delete/all`,
    ),
    reArrangeLocationOrder: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/prev/{3}/curr/{4}/services/service/reArrange`,
    ),
    reTryDeferredActivity: formatter(
      `${JOB_FILE}/monitoring/v2/activities/activity/{0}/retry-deferred`,
    ),
    deferActivity: `${JOB_FILE}/monitoring/v2/activities/activity/mark-as-deferred`,
    markAsDeliveryFailed: formatter(
      `${JOB_FILE}/monitoring/v2/activities/activity/{0}/delivery-failed`,
    ),
    saveOtherReferenceNumber: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/locations/{3}/services/{4}/{5}/other-ref-numbers/{6}`,
    ),
    saveOtherReferenceNumbers: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/locations/{3}/services/{4}/{5}/other-ref-numbers`,
    ),
    saveOrderReferenceNumber: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/locations/{3}/services/{4}/{5}/order-numbers/{6}`,
    ),
    saveOrderReferenceNumbers: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/locations/{3}/services/{4}/{5}/order-numbers`,
    ),
    deleteOtherReferenceNumber: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/locations/{3}/services/{4}/{5}/other-ref-numbers`,
    ),
    saveRemark: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/remarks`,
    ),
    saveRemarks: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/remarks/bulk`,
    ),
    deleteRemark: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/remarks`,
    ),
    getRemarks: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/remarks`,
    ),
    updateContainerLabels: formatter(
      `${JOB_FILE}/org/{0}/jobs/{1}/containers/{2}/labels`,
    ),
    getOrderRefNumbers: formatter(
      `${JOB_FILE}/org/jobs/containers/order-numbers/{0}`,
    ),
    getGpsDetails: formatter(`${JOB_FILE}/jobs/{0}/container/{1}/gps-details`),
    uploadOrderDocument: formatter(
      `${JOB_FILE}/shipment-order-spec/org/{0}/order/{1}/documents`,
    ),
    deleteAllShipmentOrderSpecs: formatter(
      `${JOB_FILE}/shipment-order-spec/org/{0}/delete-all`,
    ),
    getPercentageOfContainer: formatter(
      `${JOB_FILE}/monitoring/v2/jpm-percentage/org/{0}/job/{1}`,
    ),
  },
  fleetManager: {
    createFuelRequest: formatter(
      `${FLEET_MANAGER}/fuel/requests/request/org/{0}/vehicle/{1}`,
    ),
    createAndApproveFuelRequest: formatter(
      `${FLEET_MANAGER}/fuel/requests/request/org/{0}/vehicle/{1}/create-and-approve`,
    ),
    getFuelRequest: formatter(
      `${FLEET_MANAGER}/fuel/requests/org/{0}/state/{1}`,
    ),
    getFuelRequestById: formatter(`${FLEET_MANAGER}/fuel/requests/{0}`),
    moveToPendingFuelRequest: formatter(
      `${FLEET_MANAGER}/fuel/requests/{0}/move/PENDING`,
    ),
    rejectFuelRequest: formatter(`${FLEET_MANAGER}/fuel/requests/{0}/reject`),
    approveFuelRequest: formatter(`${FLEET_MANAGER}/fuel/requests/{0}/approve`),
    completeFuelRequest: formatter(
      `${FLEET_MANAGER}/fuel/requests/{0}/complete`,
    ),

    getResources: formatter(`${FLEET_MANAGER}/resources/{0}`),
    getOrgPartnerLocations: formatter(
      `${FLEET_MANAGER}/partner/partnersWithGeoByPartnerId/org/{0}/partnerId/{1}/organizationFunction/{2}`,
    ),
    getOrgPartnerByOrganizationFunction: formatter(
      `${FLEET_MANAGER}/partner/org/{0}/partners/organizationFunction/{1}`,
    ),

    getDefaultTeam: formatter(`${ORGANIZATION}/vehicles/managed-by`),
    getOrgPersonsByIds: `${ORGANIZATION}/org-user/org-person-ids`,
    getAllVehiclesForAGivenOrganization: formatter(
      `${ORGANIZATION}/vehicles/managed-by`,
    ),
    getAllDriversForAGivenOrganization: formatter(
      `${ORGANIZATION}/org-users/scopes/{0}`,
    ),
    getCustomerByCustomerId: formatter(`${ORGANIZATION}/organizations/{0}`),
    getPersonInRequest: `${ORGANIZATION}/org-user/org-person-ids`,
    getPersonsByType: formatter(`${ORGANIZATION}/org-users/scopes/{0}`),
    getPersonsByScopeAndReportsToIn: formatter(
      `${ORGANIZATION}/org-user/scopes/{0}/ReportsToIn/{1}`,
    ),
    getAllVehiclesManagedBy: `${ORGANIZATION}/vehicles/managed-by`,
    getPersonsByScopeAndAllManagedOrgs: formatter(
      `${ORGANIZATION}/internal-and-external-users/org/{0}/scopes`,
    ),
  },
  quotation: {
    getQuotationByOrgIdAndJobRefId: formatter(`${QUOTATION}/{0}/byjobref/{1}`),
    getQuotationByOrgIdAndJobRefIdAndOrder: formatter(
      `${QUOTATION}/{0}/{1}/job-file-quotations/order/{2}`,
    ),
    updateTheOffer: formatter(
      `${QUOTATION}/org/{0}/jobs/{1}/job-quotations/{2}/update-offer`,
    ),
    getTemporaryQuotationForContainer: formatter(
      `${QUOTATION}/org/{0}/jobs/{1}/rateCards/{2}/temporary`,
    ),
    updateQuotation: formatter(`${QUOTATION}/org/{0}/jobs/{1}/rateCards/{2}`),
    finalizeQuotation: formatter(`${QUOTATION}/org/{0}/jobs/{1}/{2}/finalize`),
    getTemporaryFinalizeQuotation: formatter(
      `${QUOTATION}/org/{0}/jobs/{1}/{2}/finalize/temporary`,
    ),
    generateContractForQuotation: formatter(
      `${QUOTATION}/org/{0}/jobs/{1}/quotation/{2}/preparedBy/{3}/generate-contract`,
    ),
    getJobWorkflowDataByOrgIdAndJobRefId: formatter(
      `${QUOTATION}/workflowpersist/getall/{0}/{1}`,
    ),
    getPdfUrl: formatter(
      `${QUOTATION}/org/{0}/jobs/{1}/job-quotations/{2}/{3}`,
    ),
    saveOffer: formatter(
      `${QUOTATION}/org/{0}/jobs/{1}/job-quotations/{2}/offer`,
    ),
    generatePdfUrlV2: formatter(
      `${QUOTATION}/v2/org/{0}/jobs/{1}/order/{2}/pdf/generate-and-save`,
    ),
    generateBookingEnginePdf: formatter(
      `${QUOTATION}/v2/booking-engine/org/{0}/jobs/{1}/pdf/generate`,
    ),
    generateSavePDF: formatter(
      `${QUOTATION}/v2/org/{0}/jobs/{1}/order/{2}/pdf/save`,
    ),
    acceptOffer: formatter(
      `${QUOTATION}/org/{0}/jobs/{1}/job-quotations/{2}/accept`,
    ),
    containerRateCard: {
      save: formatter(`${QUOTATION}/container-ratecard`),
      update: `${QUOTATION}/container-ratecard`,
      updatePost: `${QUOTATION}/container-ratecard/update`,
      saveBulk: formatter(`${QUOTATION}/container-ratecard/bulk`),
      getByJob: formatter(`${QUOTATION}/container-ratecard/job/{0}`),
      getByContainer: formatter(
        `${QUOTATION}/container-ratecard/job/{0}/container/{1}`,
      ),
      getByContainerAndOrg: formatter(
        `${QUOTATION}/container-ratecard/job/{0}/container/{1}/org/{2}`,
      ),
      delete: formatter(
        `${QUOTATION}/container-ratecard/job/{0}/container/{1}`,
      ),
    },
    saveInitQuotation: formatter(`${QUOTATION}/org/{0}/jobs/{1}/create`),
    assignSupervisorToQuotation: formatter(
      `${QUOTATION}/org/{0}/jobs/{1}/job-quotations/{2}/assign-supervisor-to-quotation`,
    ),
    getQuotationPDFUrl: formatter(`${QUOTATION}/v2/org/{0}/jobs/{1}/order/{2}`),
    getQuotationPDFUrlWithCurrency: formatter(
      `${QUOTATION}/v2/org/{0}/jobs/{1}/order/{2}/isDefaultCurrency/{3}/pdf`,
    ),
    getQuotationPDFPreSignedUrlByRefId: formatter(
      `${QUOTATION}/v2/org/{0}/jobs/{1}/ref/{2}/pdf/pre-signed`,
    ),
    getLoadQuotationById: formatter(`${QUOTATION}/load-quotations/{0}`),
    getBookingEnginePdfRefByJobRefId: formatter(
      `${QUOTATION}/load-quotations/org/{0}/jobs/{1}/pdf-reference`,
    ),
    getTemporaryLoadQuotation: formatter(
      `${QUOTATION}/load-quotation/rootJob/{0}/serviceProvider/{1}/jobs/{2}/rootJobRef/{3}/container/{4}/temporary`,
    ),
    saveLoadQuotation: formatter(
      `${QUOTATION}/load-quotation/rootJob/{0}/serviceProvider/{1}/jobs/{2}/rootJobRef/{3}/container/{4}`,
    ),
    saveLoadQuotationOnBehalfOf: formatter(
      `${QUOTATION}/load-quotation/rootJob/{0}/serviceProvider/{1}/customer/{2}/jobs/{3}/rootJobRef/{4}/container/{5}`,
    ),
    getContainerRateCardByBidIdRFQIdRateCardId: formatter(
      `${QUOTATION}/container-ratecard/bid/{0}/rfq/{1}/ratecard/{2}`,
    ),
  },
  APP_ROUTE: {
    getRoute: `${FRONTEND_V4}`,
    getRoutePostAbans: `${FRONTEND_V4}`,
    getRouteMasterData: `${FRONTEND_MASTER_DATA_V8}`,
  },
  jpm: {
    makeAsDoneTask: `${JPM}/task/complete`,
    reportIncident: `${JPM}/incidents/incident/report`,
    getReportIncidents: formatter(
      `${JPM}/incidents/jpm/{0}/leg/{1}/activity/{2}`,
    ),
    updateIncidentAction: formatter(
      `${JPM}/incidents/incident/{0}/action/{1}/previous-work-order/{2}`,
    ),
    getJpmById: formatter(`${JPM}/{0}`),
    getVehiclesWithJobs: `${JPM}/gps-module/vehicles/with-jobs`,
    // "on a job right now" read from the Jpm's own started/completed flags. The endpoint above infers
    // it from resourceLogs completion timestamps and reports idle vehicles as busy; it stays for its
    // existing callers.
    getVehiclesWithActiveJobs: `${JPM}/gps-module/vehicles/with-active-jobs`,
    getJpms: formatter(`${JPM}/{0}/byjobref/{1}`),
    getAllowedJpmByJpmIdAndPrincipalId: formatter(`${JPM}/jpms/summary/{0}`),
    getAllowedJpmByJobRefIdAndContainerId: formatter(
      `${JPM}/jpms/by-container/{0}/{1}`,
    ),
    getJpmByOrgIdAndJobRefIdAndContainerId: formatter(
      `${JPM}/{0}/jobRefId/{1}/containerId/{2}`,
    ),
    terminateJob: `${JPM}/terminate-job`,
    getActivityAuditLog: formatter(`${JPM}/jpms/{0}/logs`),
    deleteContainer: formatter(
      `${JPM}/remove-container/org/{0}/job/{1}/container/{2}/prepared-by/{3}`,
    ),
    createRuntimeCost: formatter(
      `${JPM}/{0}/expenses/expense/create-and-confirm`,
    ),
    removeRuntimeCost: formatter(`${JPM}/{0}/costs/{1}`),
    updateRuntimeCost: formatter(`${JPM}/{0}/costs`),
    confirmExpense: formatter(`${JPM}/{0}/expenses/expense/confirm`),
    rejectExpense: formatter(`${JPM}/{0}/expenses/{1}/reject`),
    removeExpense: formatter(`${JPM}/{0}/expenses/{1}`),
    getAllIncidents: formatter(`${JPM}/incidents/jpm/{0}`),
    deleteIncident: formatter(`${JPM}/incidents/{0}`),
    modifyJobDistance: formatter(
      `${JPM}/task/complete/modify/jpm/{0}/job-distance`,
    ),
    terminateContainer: formatter(
      `${JPM}/org/{0}/job/{1}/container/{2}/terminate-container`,
    ),
    getJPMDocuments: formatter(`${JPM}/org/{0}/jobs/{1}/documents`),
    getDropDownValues: `${JPM}`,
    downloadRunSheet: formatter(
      `${JPM}/job-reports/org/{0}/jobs/{1}/jpms/{2}/run-sheet/pdf`,
    ),
    getInternalContainers: formatter(
      `${JPM}/org/{0}/jobs/{1}/internal-containers`,
    ),
    getSubContractContainers: formatter(
      `${JPM}/org/{0}/jobs/{1}/sub-contractor/{2}/sub-contract-containers`,
    ),
    getSubContractedSummaries: formatter(
      `${JPM}/org/{0}/jobs/{1}/sub-contracted-detail-list`,
    ),
    getPendingContainers: formatter(
      `${JPM}/org/{0}/jobs/{1}/pending-containers`,
    ),
    getContainersStatus: formatter(`${JPM}/org/{0}/jobs/{1}/containers-status`),
    saveSubContractDetails: `${JPM}/task/sub-contract-details`,
    schedulePoolJob: `${JPM}/task/schedule-repo-removal`,
    markAsComplete: formatter(`${JPM}/org/{0}/job/{1}/mark-as-complete`),
    markAsUnDone: formatter(
      `${JPM}/task/complete/work-orders/{0}/mark-as-undone`,
    ),
    markAsCancel: formatter(
      `${JPM}/task/complete/work-orders/{0}/completion-time/{1}/mark-as-cancel`,
    ),
    markAsInvoke: formatter(
      `${JPM}/task/complete/work-orders/{0}/mark-as-invoke`,
    ),
    setWorkOrderGeoCoordinate: formatter(
      `${JPM}/task/complete/jpm/{0}/work-order/{1}/vehicle/{2}/set-geo-coordinate-from-web`,
    ),
    getJobCompletionStatus: formatter(
      `${JPM}/org/{0}/jobs/{1}/job-completion-status`,
    ),
    coupleContainer: `${JPM}/container/couple`,
    deCoupleContainer: `${JPM}/container/deCouple`,
    getAllAllowedJPMs: `${JPM}/collaborators/jpms`,
    loadSharedJobs: formatter(`${JPM}/collaborator/jobs`),
    loadSharedJobCounts: formatter(`${JPM}/collaborator/jobs/count`),
    getLoadsOfJob: formatter(`${JPM}/org/{0}/jobs/{1}/container/list`),
    filterLiveMapJobs: `${JPM}/collaborator/live-map/jobs`,
    getCompletedJpmDistances: formatter(
      `${JPM}/jobs/{0}/completed-loads-distances`,
    ),
    updateCompletedJpmDistances: formatter(
      `${JPM}/jobs/{0}/container/{1}/update-jpm-distances`,
    ),
    getJpmsByVehicleAndDateRange: `${JPM}/gps-module/jpms/filtered-by-vehicle`,
    getAllJpmsByVehicleAndDateRange: `${JPM}/gps-module/jpms/filtered-by-vehicle/all`,
    getRunSheetData: formatter(`${JPM}/org/{0}/jobs/{1}/run-sheet`),
    getJpmIdsByJobRefId: formatter(`${JPM}/org/{0}/jobs/{1}/jpmIds`),
  },
  dynamicCt: {
    // ms-jobfile (work-order / activity mutations)
    plannedVsActual: formatter(
      `${JOB_FILE}/monitoring/v2/dynamic-ct/jpm/{0}/planned-vs-actual`,
    ),
    reconcile: formatter(
      `${JOB_FILE}/monitoring/v2/dynamic-ct/jpm/{0}/reconcile/{1}`,
    ),
    // ms-control-tower (JPM lifecycle state)
    status: formatter(`${JPM}/{0}/dynamic-ct/status`),
    resume: formatter(`${JPM}/{0}/dynamic-ct/resume`),
    onHold: formatter(`${JPM}/{0}/dynamic-ct/on-hold`),
    markAsComplete: formatter(`${JPM}/{0}/dynamic-ct/mark-as-complete`),
    vehicleActiveJpm: formatter(
      `${JPM}/dynamic-ct/vehicles/{0}/active-jpm`,
    ),
    // ms-telematics (raw GPS visit log of the engine)
    visits: formatter(`${FM_GPS}/dct/v1/jpm/{0}/visits`),
  },
  controlTower: {
    jobProgress: formatter(`${CONTROL_TOWER}/?jobRefId={0}&containerId={1}`),
    terminateJob: `${CONTROL_TOWER}/terminate-job`,
    getRunningSheetData: formatter(
      `${CONTROL_TOWER}/org/{0}/jobs/{1}/running-sheet`,
    ),
  },
  s3: {
    uploadFile: formatter(`${S3}/upload-files/{0}/{1}/{2}/{3}/{4}`),
    uploadFileV2: formatter(`${S3}/upload-files/v2/{0}/{1}/{2}/{3}/{4}`),
    getFile: `${S3}/getSpecificFile`,
    getDocuments: formatter(`${S3}/files/get-documents/{0}`),
    getPreSignedUrls: formatter(`${S3}/pre-signed-urls/jpm/{0}`),
  },
  youAreHere: {
    getPDF: {
      getDocumentLink: formatter(`${YOU_ARE_HERE}/url`),
    },
  },
  gateway: {
    getLocations: `${GATEWAY}/api/location`,
    getLocationsByType: formatter(`${GATEWAY}/api/location/{0}`),
  },
  rateCard: {
    getRateCards: `${RATE_CARD}/ratecards`,
    getRateCardsForOperation: `${RATE_CARD}/ratecards/get-data-operation`,
    getRateCardsByFilters: formatter(
      `${RATE_CARD}/ratecards/orgId/{0}/pageNo/{1}/pageSize/{2}/by-filters`,
    ),
    getRateCardsByCustomerId: formatter(`${RATE_CARD}/ratecards/customer/{0}`),
    getRateCardsByOperationList: formatter(
      `${RATE_CARD}/ratecards/orgId/{0}/pageNo/{1}/pageSize/{2}/filter-by-operation`,
    ),
    searchRateCards: formatter(
      `${RATE_CARD}/ratecards/orgId/{0}/pageNo/{1}/pageSize/{2}/search`,
    ),
    getRateCardsByFilter: formatter(
      `${RATE_CARD}/ratecards/orgId/{0}/by-filters`,
    ),
    getRateSheetsForMainOrgRelated: `${RATE_CARD}/ratecards/main-org-related`,
    getCost: formatter(`${RATE_CARD}/costs/search?keyword={0}`),
    getAllTiers: formatter(`${RATE_CARD}/tier/getall/{0}`),
    getPaginateRateTiers: formatter(
      `${RATE_CARD}/tier/get-paginate-rate-tiers/{0}`,
    ),
    saveTier: `${RATE_CARD}/tier`,
    updateTier: `${RATE_CARD}/tier`,
    removeTier: formatter(`${RATE_CARD}/tier/{0}`),
    saveRateCard: `${RATE_CARD}/ratecard`,
    deleteRateCard: formatter(`${RATE_CARD}/ratecard/{0}`),
    setExchangeRate: formatter(
      `${RATE_CARD}/rateCard/{0}/exchangeRate/{1}/set-exchange-rate`,
    ),
    getRateCardsByIds: `${RATE_CARD}/ratecards/by-ids`,
    findCommitmentSuppliers: formatter(
      `${RATE_CARD}/ratecard/org/{0}/contractType/{1}/from/{2}/to/{3}/organization_ids`,
    ),
    saveOperationDistanceMatrix: `${RATE_CARD}/operation/distance-matrix-template`,
    getOperationDistanceMatrix: formatter(
      `${RATE_CARD}/operation/distance-matrix-template/{0}/orgId/{1}`,
    ),
    getDistanceMatrixOperationList: formatter(
      `${RATE_CARD}/operation-list/distance-matrix-template/orgId/{0}`,
    ),
    searchRateCardsForSpotRFQBidding: `${RATE_CARD}/ratecards/minimal`,
    getRateCardByRateCardId: formatter(`${RATE_CARD}/ratecard/{0}`),
    uploadRateSheet: `${RATE_CARD}/upload`,
    getUploadContractsByType: `${RATE_CARD}/upload/contracts/by-type`,
    prepareBulkSaveRateSheets: `${RATE_CARD}/upload/prepare-bulk-save`,
    bulkSaveRateSheets: `${RATE_CARD}/upload/bulk-save`,
    updateUploadContract: `${RATE_CARD}/upload/contracts/update`,
    downloadErrorExcel: `${RATE_CARD}/upload/errors/download`,
    exportContractsExcel: formatter(
      `${RATE_CARD}/orgId/{0}/contracts/export-excel`,
    ),
    exportSingleContractExcel: formatter(
      `${RATE_CARD}/contracts/{0}/export-excel`,
    ),
  },
  smartContract: {
    generateContract: formatter(
      `${SMART_CONTRACT}/smartContractPdf/byorg/{0}/jobref/{1}/acceptedQuotationId/{2}/preparedBy/{3}`,
    ),
    getAllContracts: formatter(`${SMART_CONTRACT}/byorg/{0}/jobref/{1}`),
    getSmartContractVersions: formatter(`${SMART_CONTRACT}/{0}/{1}/versions`),
    getLatestContractView: formatter(
      `${SMART_CONTRACT}/v2/org/{0}/jobs/{1}/versions/latest/view`,
    ),
    getSpecificContractView: formatter(
      `${SMART_CONTRACT}/v2/org/{0}/jobs/{1}/versions/{2}/view`,
    ),
    getDownloadContractPDF: formatter(
      `${SMART_CONTRACT}/v2/org/{0}/jobs/{1}/versions/{2}/pdf/pre-signed-url`,
    ),
    getContractPreSignedFile: formatter(
      `${SMART_CONTRACT}/v2/org/{0}/jobs/{1}/versions/{2}/pdf/pre-signed`,
    ),
    generateAndSavePDF: formatter(
      `${SMART_CONTRACT}/v2/org/{0}/jobs/{1}/versions/{2}/pdf/generate/save`,
    ),
    confirmLoadLevelBookingEngine: formatter(
      `${SMART_CONTRACT}/v2/booking-engine/contract/org/{0}/job/{1}/customer/org/{2}/job/{3}/root/{4}/sp/{5}/load/{6}/order/{7}/confirm/{8}/prepared-by/{9}`,
    ),
    // NOTE: bulk endpoint — needs to be implemented on the ms-smart-contract service.
    // Body: { loads: { loadId: string; order: number }[] }
    confirmLoadLevelBookingEngineBulk: formatter(
      `${SMART_CONTRACT}/v2/booking-engine/contract/org/{0}/job/{1}/customer/org/{2}/job/{3}/root/{4}/sp/{5}/confirm/{6}/prepared-by/{7}/bulk`,
    ),
    getCustomerAndSupplierContracts: formatter(
      `${SMART_CONTRACT}/v2/org/{0}/jobs/{1}/loads/{2}/contracts`,
    ),
  },
  logSheet: {
    getLogSheetsByLoad: formatter(
      `${JOB_FILE}/log-sheets/org/{0}/jobs/{1}/loads/{2}`,
    ),
    downloadLogSheetVersion: formatter(
      `${JOB_FILE}/log-sheets/org/{0}/jobs/{1}/loads/{2}/versions/{3}/download`,
    ),
  },
  assets: {
    getHSCodes: './assets/json-data/hsCodes.json',
    getContainerTypesAndSizes: './assets/json-data/containerTypeAndSize.json',
    getInvoiceCosts: './assets/json-data/invoice-costs.json',
    getConfigRelevantToWorkFlow: formatter(
      `./assets/json-data/add-container-configurations/{0}.json`,
    ),
    getErrorMessage: './assets/json-data/error-messages.json',
    getCostTypeList: './assets/json-data/invoice-costs.json',
  },
  google: {
    apiUrl:
      'https://maps.googleapis.com/maps/api/staticmap?size=150x100&scale=2&maptype=roadmap&style=feature:all&path=weight:1%7Ccolor:0xff0000ff%7Cgeodesic:true%7Cenc:',
  },
  fmGPS: {
    getGpsInfoListAboveLogDate: formatter(
      `${FM_GPS}/trips/v1/jpm/{0}/logDate/{1}/`,
    ),
    getEncodedPathDataFromCompletedTrips: formatter(
      `${FM_GPS}/trips/v1/encodedTripPath/jpm/{0}/completed`,
    ),
    getEncodedPathDataFromOngoingTrip: formatter(
      `${FM_GPS}/trips/v1/encodedTripPath/jpm/{0}/currentDate/{1}/ongoing`,
    ),
    getEncodedPathsOfJPMsByJPMIds: `${FM_GPS}/trips/v1/jpms/encoded-trip-paths`,
    getUnplannedStops: formatter(`${FM_GPS}/trips/v1/jpm/{0}/unplanned-stops`),
    getSpeedsOfVehicle: formatter(
      `${FM_GPS}/vehicle/v1/{0}/startDate/{1}/currentDate/{2}/speeds`,
    ),
    getLastGpsInfo: formatter(
      `${FM_GPS}/trips/v1/lastGpsInfo/jpm/{0}/currentDate/{1}`,
    ),
    restartLocationServiceOfVehicle: formatter(
      `${FM_GPS}/vehicle/v1/{0}/restartLocationService`,
    ),
    getAllDevices: `${FM_GPS}/gps-devices/v1`,
    updateGpsDevice: `${FM_GPS}/gps-devices/v1/update`,
    updateDeviceMappingCache: formatter(
      `${FM_GPS}/gps-devices/v1/{0}/vehicle-mapping/data/refresh`,
    ),
    getGpsLogData: formatter(`${FM_GPS}/gps-devices/v1/{0}/gps-log-data`),
    getAllActiveDevices: `${FM_GPS}/gps-devices/v1/active-gps-device`,
    getAllRemovedDevices: `${FM_GPS}/gps-devices/v1/removed-gps-device`,
    getAllUnAssignedDevices: `${FM_GPS}/gps-devices/v1/unassigned-gps-device`,
    changeActiveStatusGpsDevice: formatter(
      `${FM_GPS}/gps-devices/v1/{0}/gps-change-status/device`,
    ),
    changeGpsDeviceAssignVehicle: formatter(
      `${FM_GPS}/gps-devices/v1/{0}/gps-change-assign/vehicle`,
    ),
    updateGpsDeviceAssignVehicle: formatter(
      `${FM_GPS}/gps-devices/v1/{0}/{1}/gps-update-assign/vehicle`,
    ),
    getEncodedPathByVehicle: formatter(
      `${FM_GPS}/trips/v1/encodedTripPath/jpm/{0}/vehicle/{1}/currentDate/{2}/ongoing-by-vehicle`,
    ),
    getEncodedPathByVehicleAndDateRange: `${FM_GPS}/trips/v1/encoded-paths/vehicle-ids`,
    getAllGeoFences: `${FM_GPS}/geo-fence/v1`,
    createGeoFence: `${FM_GPS}/geo-fence/v1`,
    deleteGeoFenceByName: `${FM_GPS}/geo-fence/v1`,
    getGeoFenceCrossingTime: `${FM_GPS}/gps-data/v1/find-geo-fence-crossing-time`,
    findGeoFenceCrossingTimeForReports: `${FM_GPS}/gps-data/v1/find-geo-fence-crossing-time-for-reports`,
    findGeoFenceCrossingTimeForReportsWithPagination: `${FM_GPS}/gps-data/v1/find-geo-fence-crossing-time-for-reports-paginated`,
    findClosestApproachGeoFenceTime: `${FM_GPS}/gps-data/v1/find-closest-approach-geo-fence-time`,
    findClosestApproachGeoFenceTimeWithPagination: `${FM_GPS}/gps-data/v1/find-closest-approach-geo-fence-time-paginated`,
    findUtilizationVehicleGpsReport: `${FM_GPS}/gps-data/v1/find-utilization-vehicle-gps-report`,
    findUtilizationVehicleGpsReportWithPagination: `${FM_GPS}/gps-data/v1/find-utilization-vehicle-gps-report-paginated`,
  },
  fileHandelar: {
    thumbnail: `${FILE_HANDLER}/v1/files/thumbnail/default/pre-signed`,
    imageUrl: formatter(`${FILE_HANDLER}/v1/files/{0}/pre-signed`),
    fetchThumbnail: formatter(
      `${FILE_HANDLER}/v1/files/{0}/thumbnail/default/pre-signed`,
    ),
    fetchThumbnails: `${FILE_HANDLER}/v1/files/thumbnail/default/pre-signed`,
    uploadFile: formatter(`${FILE_HANDLER}/v1/files/file/org/{0}/path/{1}`),
    uploadFileStream: formatter(
      `${FILE_HANDLER}/v1/files/file/stream/org/{0}/path/{1}`,
    ),
    getPreSignedUrl: formatter(`${FILE_HANDLER}/v1/files/{0}/pre-signed`),
    getFilesByOrgAndOrder: formatter(
      `${FILE_HANDLER}/v1/files/org/{0}/order/{1}/files`,
    ),
    deleteFileMetadata: formatter(`${FILE_HANDLER}/v1/files/{0}/metadata`),
  },
  fileConverter: {
    getJobMasterExcel: `${FILE_CONVERTER}/v1/convert/jobmaster`,
  },
  workflow: {
    // getWorkflowConfigByWorkflowId: formatter(`${WORKFLOW}/workflow/getall/{0}/{1}`),
    // getWorkflowData: formatter(`${WORKFLOW}/workflowpersist/getall/{0}/{1}`),
    getWorkFlowSteps: formatter(`${JOB_FILE}/workflow/steps/{0}`),
    getCustomsChannels: formatter(`${JOB_FILE}/workflow/{0}/customs-channel`),
  },
  masterData: {
    masterDataKPIExcelReportUrl: formatter(
      `${MASTER_DATA}/reports/excel/kpi/from/{0}/to/{1}/orgId/{2}`,
    ),
    getWorkFlowSteps: formatter(`${JOB_FILE}/workflow/steps/{0}`),
    getCustomsChannels: formatter(`${JOB_FILE}/workflow/{0}/customs-channel`),
  },
  invoice: {
    generateInvoice: formatter(`${INVOICE}/v2/org/{0}/job/{1}/user/{2}/pdf`),
    getInvoice: formatter(`${INVOICE}/v2/org/{0}/job/{1}`),
    downloadInvoicePdf: formatter(
      `${INVOICE}/v2/org/{0}/job/{1}/downloadInvoicePdf`,
    ),
  },
  locations: {
    saveOrganizationLocation: `${LOCATIONS}/locations/`,
    getOrganizationLocation: formatter(`${LOCATIONS}/locations/{0}`),
    // getOrganizationLocationsByOrg: formatter(`${LOCATIONS}`),
    deleteOrganizationLocation: formatter(`${LOCATIONS}/locations/{0}`),
    getLocationDefinitionByParentLocationType: formatter(
      `${LOCATIONS}/definitions/by-type/{0}`,
    ),
    getLocationDefinitions: `${LOCATIONS}/definitions`,
    getLocationsByFilter: `${LOCATIONS}/locations`,
    getLocationsByFilterPaginated: `${LOCATIONS}/locations/paginated`,
    getLocationsForWizard: `${LOCATIONS}/locations/wizard`,
    getLocationReferenceIdAvailability: `${LOCATIONS}/locations/availability`,
    getLocationsForWizardAll: `${LOCATIONS}/locations/wizard-all`,
    getLocationsForWizardExcludeExternal: `${LOCATIONS}/locations/wizard-exclude-external`,
    getLocationsByIds: `${LOCATIONS}/locations/ids`,
  },
  organization: {
    getVehicleCategoryWithDimension: `${ORGANIZATION}/meta-data/vehicle-category-with-dimension`,
    saveOrganization: `${ORGANIZATION}/organizations/`,
    bulkUpload: `${ORGANIZATION}/organizations/bulk-upload-async`,
    getOrganization: formatter(`${ORGANIZATION}/organizations/{0}`),
    getOrgNameById: formatter(`${ORGANIZATION}/organizations/{0}/name`),
    getOrgContactDetailsById: formatter(
      `${ORGANIZATION}/organizations/{0}/contact`,
    ),
    getPartnerOrganization: `${ORGANIZATION}/organizations/`,
    getCommonOrganizations: `${ORGANIZATION}/organizations/common`,
    getPaginatedCommonOrganizations: `${ORGANIZATION}/organizations/common/paginate`,
    getOnBoardedOrganizations: `${ORGANIZATION}/organizations/on-boarded`,
    getPaginatedOnBoardedOrganizations: `${ORGANIZATION}/organizations/on-boarded/paginate`,
    getOrganizationsByType: formatter(
      `${ORGANIZATION}/organizations/orgType/{0}`,
    ),
    getPartnerOrganizations: `${ORGANIZATION}/organizations/partners`,
    getPartnerOrganizationsByOperations: `${ORGANIZATION}/organizations/partners/operations`,
    getPaginatedPartnerOrganizationsByOperations: `${ORGANIZATION}/organizations/partners/operations/paginate`,
    getPartnerOrganizationsForBehalf: `${ORGANIZATION}/organizations/partners/behalf`,
    getOnBoardedOrganizationsForBehalf: `${ORGANIZATION}/organizations/on-boarded/behalf`,
    searchPartnerOrganizationsByOperations: `${ORGANIZATION}/organizations/partners/operations/search`,
    getPartnerOrgsByVerticals: `${ORGANIZATION}/organizations/partners/by-verticals`,
    getChildrenOrganizations: formatter(
      `${ORGANIZATION}/organizations/{0}/children`,
    ),
    getPaginatedChildrenOrganizations: formatter(
      `${ORGANIZATION}/organizations/{0}/children/paginate`,
    ),
    getAllOnBoardedAndPartnerOrgsByVertical: formatter(
      `${ORGANIZATION}/organizations/by-vertical/{0}`,
    ),
    getOrganizationsByVertical: formatter(
      `${ORGANIZATION}/organizations/managedBy/by-vertical/{0}`,
    ),
    getOnBoardedOrganizationsByVertical: formatter(
      `${ORGANIZATION}/organizations/managedBy/by-vertical/{0}/system-org`,
    ),
    deleteOrganizationLocation: formatter(`${ORGANIZATION}/organizations/{0}`),
    getVerticalInfos: `${ORGANIZATION}/organizations/vertical-info/`,
    saveHumanResource: `${ORGANIZATION}/users/`,
    updateOrganizationUser: `${ORGANIZATION}/org-users/`,
    getHumanResourcesByOrg: formatter(`${ORGANIZATION}/users/org/{0}`),
    // LinkedIn is stored on the individuals record, not on org-user, so the
    // human-resource edit dialog reads it from here.
    getIndividualById: formatter(`${ORGANIZATION}/individuals/{0}`),
    getHumanResourcesByOrgAndScopes: formatter(
      `${ORGANIZATION}/users/org/{0}/scopes`,
    ),
    deleteHumanResourceOfOrganization: formatter(
      `${ORGANIZATION}/org-user/{0}`,
    ),
    getOrgUserByUserId: formatter(`${ORGANIZATION}/org-user/{0}`),
    getOrgsByTypeAndId: formatter(`${ORGANIZATION}/organizations/{0}/type/{1}`),
    getVehiclesByResourceTypeAndVehicleTypes: formatter(
      `${ORGANIZATION}/vehicles/searchForIds/managedType/{0}`,
    ),
    getVehiclesByOrg: `${ORGANIZATION}/vehicles/managed-by`,
    getVehiclesByManagingOrg: formatter(
      `${ORGANIZATION}/vehicles/managed-by-org/{0}`,
    ),
    getVehiclesByManagingOrgAndPartnersOrg: formatter(
      `${ORGANIZATION}/vehicles/managed-by-org-and-partners/{0}`,
    ),
    getVehiclesByManagingOrgFilter: formatter(
      `${ORGANIZATION}/vehicles/managed-by-org/{0}/filter`,
    ),
    getVehiclesByManagingOrgAndIds: formatter(
      `${ORGANIZATION}/vehicles/managed-by-org/{0}/vehicles/{1}`,
    ),
    saveVehicle: `${ORGANIZATION}/vehicles/`,
    deleteVehicle: formatter(`${ORGANIZATION}/vehicles/{0}`),
    getRolesByOrgId: formatter(`${ORGANIZATION}/roles/org/{0}`),
    createRole: `${ORGANIZATION}/roles/`,
    updateRole: `${ORGANIZATION}/roles/`,
    deleteRole: formatter(`${ORGANIZATION}/individual/{0}`),
    getOrgPartners: formatter(`${ORGANIZATION}/organizations/{0}/type/{1}`),
    OrgUsersByReportsToInAndScopesIn: formatter(
      `${ORGANIZATION}/org-user/scopes/{0}/ReportsToIn/{1}`,
    ),
    getOrgLogo: formatter(`${ORGANIZATION}/organizations/{0}/get-logo`),
    getOrgUserByScopes: formatter(
      `${ORGANIZATION}/users/scopes?pageNum={0}&pageSize={1}`,
    ),
    getOrgLogos: `${ORGANIZATION}/organizations/logos`,
    getOrgDetails: `${ORGANIZATION}/organizations/customers`,
    getCurrencies: `${ORGANIZATION}/countries/currencies`,
    getCountryById: formatter(`${ORGANIZATION}/countries/{0}`),
    getCountries: `${ORGANIZATION}/countries`,
    getHolidaysByCountryAndYear: formatter(
      `${ORGANIZATION}/countries/countryCode/{0}/year/{1}`,
    ),
    checkOrgShortNameAvailability: formatter(
      `${ORGANIZATION}/organizations/{0}/availability`,
    ),
    checkVehicleAvailability: formatter(
      `${ORGANIZATION}/vehicles/{0}/availability`,
    ),
    // TODO: refactor this API to and paginate
    getOrganizationsByOrgIdAndOrgType: formatter(
      `${ORGANIZATION}/organizations/get-related-organizations/all/{0}/organizationType/{1}`,
    ),
    getPaginatedOrganizationsByOrgIdAndOrgType: formatter(
      `${ORGANIZATION}/organizations/get-related-organizations/all/{0}/organizationType/{1}/paginate`,
    ),
    getOrgPersonsOfVertical: formatter(`${ORGANIZATION}/users/org/{0}/scopes`),
    getCollabLogoMap: `${ORGANIZATION}/organizations/logos-map`,
    getOrgPersonsByIds: `${ORGANIZATION}/org-user/org-person-ids`,
    getLiveMapCustomers: formatter(
      `${ORGANIZATION}/organizations/customers/org/{0}/user/{1}`,
    ),
    getLiveMapServiceProviders: formatter(
      `${ORGANIZATION}/organizations/service-providers/org/{0}/user/{1}`,
    ),
    getOrganizationInvoiceLoadsLimit: formatter(
      `${ORGANIZATION}/organizations/{0}/invoice-loads-limit`,
    ),
    getOperations: `${ORGANIZATION}/operations/`,
    getOperationSpeedLimits: formatter(
      `${ORGANIZATION}/operations/{0}/geo-fence-speeds/all`,
    ),
    saveOperationSpeedLimits: formatter(
      `${ORGANIZATION}/operations/{0}/geo-fence-speeds`,
    ),
    deleteOperationSpeedLimits: formatter(
      `${ORGANIZATION}/operations/{0}/geo-fence-speeds/{1}/delete`,
    ),
    updateAndAddOperation: `${ORGANIZATION}/operations/`,
    getCurrentUserOperations: `${ORGANIZATION}/operations/current-user`,
    getOperationLabels: formatter(`${ORGANIZATION}/operations/{0}/labels`),
    syncOperationLabels: formatter(`${ORGANIZATION}/operations/{0}/labels/sync`),
    getLabelsForOperations: `${ORGANIZATION}/operations/labels`,
    getVehicleCategories: `${ORGANIZATION}/meta-data/vehicle-category`,
    getOrgIdsByOrgType: `${ORGANIZATION}/organizations/wizard/related-org-ids`,
    getSharedVehicleData: formatter(
      `${ORGANIZATION}/live-maps/shared-org-infos/{0}`,
    ),
    getAllSharedVehicleData: formatter(
      `${ORGANIZATION}/live-maps/org/{0}/shared-org-infos`,
    ),
    saveSharedOrgData: `${ORGANIZATION}/live-maps/shared-org-info`,
    getShareableVehicleData: formatter(
      `${ORGANIZATION}/vehicles/internal-and-partner/{0}`,
    ),
    getUsersOfOrg: formatter(`${ORGANIZATION}/users/org/{0}/pagination`),
    removeSharedVisibilityByOrg: formatter(
      `${ORGANIZATION}/live-maps/org/{0}/visibility/remove`,
    ),
    removeSharedVisibilityByVehicle: formatter(
      `${ORGANIZATION}/live-maps/org/{0}/by-vehicle/visibility/remove`,
    ),
    getUniqueSharedVehicles: formatter(
      `${ORGANIZATION}/live-maps/org/{0}/unique-shared-vehicles`,
    ),
    getFilteredOrgsByVehicles: `${ORGANIZATION}/live-maps/shared-orgs-of-vehicles`,
    getUsersOfSelectedSharedOrg: formatter(
      `${ORGANIZATION}/live-maps/org/{0}/users-of-vehicles-by-org`,
    ),
    updateSmsTypes: formatter(
      `${ORGANIZATION}/workflow-configs/{0}/operations/{1}/update-sms-types}`,
    ),
    getSmsTypeStatus: formatter(
      `${ORGANIZATION}/workflow-configs/{0}/operations/{1}`,
    ),
    getVehicleCategoryIdByName: `${ORGANIZATION}/meta-data/vehicle-category/get-id-by-name`,
    updateFuelRates: formatter(
      `${ORGANIZATION}/workflow-configs/{0}/operations/{1}/fuel-rates`,
    ),
    getFuelRates: formatter(
      `${ORGANIZATION}/workflow-configs/{0}/operations/{1}/fuel-rates`,
    ),
    deleteFuelRates: formatter(
      `${ORGANIZATION}/workflow-configs/{0}/operations/{1}/fuel-rates/{2}`,
    ),
    updateFuelRateContracts: formatter(
      `${ORGANIZATION}/workflow-configs/{0}/operations/{1}/fuel-rates-contracts`,
    ),
    getVehiclesManagedByOrgAndPartnersForGpsModule: `${ORGANIZATION}/vehicles/gps/module/managed-by-org-and-partners`,
    getVehiclesForGpsModule: `${ORGANIZATION}/vehicles/gps/module/vehicles`,
    getTrailersOfOrgAndPartners: formatter(`${ORGANIZATION}/vehicles/trailers/org/{0}`),
    getGpsManagerFirstTimeVisit: `${ORGANIZATION}/org-user/gps-manager-first-time-visit`,
    setGpsManagerFirstTimeVisit: `${ORGANIZATION}/org-user/gps-manager-first-time-visit`,
    removeGpsManagerFirstTimeVisit: `${ORGANIZATION}/org-user/gps-manager-first-time-visit/remove`,
    getPartnerOrgIdsByManagedOrgId: formatter(
      `${ORGANIZATION}/organizations/partners/managed-by/{0}/ids`,
    ),
    downloadBulk: formatter(
      `${ORGANIZATION}/organizations/org/{0}/bulk-download`,
    ),
    updateDashboardRows: formatter(
      `${ORGANIZATION}/organizations/org/{0}/user/{1}/dashboard-rows`,
    ),
    getOrganizationWorkflowConfigByIds: formatter(
      `${ORGANIZATION}/workflow-configs/{0}/workflows/{1}`,
    ),
    removeFuelRateEquationForOperations: formatter(
      `${ORGANIZATION}/workflow-configs/{0}/workflows/{1}/ratecard/{2}/remove-fuel-rate-equation`,
    ),
    getDigitalTwinVehicles: formatter(
      `${ORGANIZATION}/vehicles/trucks/vehicle-categories/digital-twin`,
    ),
    getDigitalTwinVehiclesByOrg: formatter(
      `${ORGANIZATION}/vehicles/trucks/vehicle-categories/digital-twin/orgs`,
    ),
    // Same vehicles as the endpoint above, trimmed to the fields the load-management digital-twin
    // dialog renders and with each group's orgName resolved server-side, so the dialog does not have
    // to follow up with findOrgByIds. The untrimmed endpoint stays for its other consumers.
    getDigitalTwinVehiclesByOrgSlim: formatter(
      `${ORGANIZATION}/vehicles/trucks/vehicle-categories/digital-twin/orgs/slim`,
    ),
    // Paginated digital-twin selector (vehicle pagination plan, phase 0/1)
    getDigitalTwinCategorySummary: formatter(
      `${ORGANIZATION}/vehicles/trucks/vehicle-categories/digital-twin/summary`,
    ),
    getDigitalTwinVehiclesPage: formatter(
      `${ORGANIZATION}/vehicles/trucks/vehicle-categories/digital-twin/page`,
    ),
    getDigitalTwinVehicleIds: formatter(
      `${ORGANIZATION}/vehicles/trucks/vehicle-categories/digital-twin/ids`,
    ),
    getVehiclesByIds: formatter(
      `${ORGANIZATION}/vehicles/by-ids`,
    ),
    findOrgByIds: `${ORGANIZATION}/organizations/find-organization/ids`,
    getInternalExternalVehicles: formatter(
      `${ORGANIZATION}/vehicles/trucks/internal-external`,
    ),
    getRelatedOrganizationsIdAndNames: formatter(
      `${ORGANIZATION}/organizations/get-related-organizations-id-and-names/{0}/organizationType/{1}`,
    ),
    getTenantKeyByOrgId: formatter(
      `${ORGANIZATION}/multi-tenancy/tenant-details/org/{0}/tenant-key`,
    ),
  },
  report: {
    getLoadManifests: formatter(
      `${REPORT}/v1/load-manifest/org/{0}/jobRefId/{1}/excel-file`,
    ),
    getVehicleStatusReport: `${REPORT}/v1/gps-report/vehicle/status/excel-file`,
    getLocationTimeStampReport: `${REPORT}/v1/gps-report/location/timestamp/excel-file`,
    getClosestApproachGeoFenceReport: `${REPORT}/v1/gps-report/closest-approach/geofence/excel-file`,
    getStopTimeAnalysisReport: `${REPORT}/v1/gps-report/stop/time/analysis/excel-file`,
    getVehicleUtilisationReport: `${REPORT}/v1/gps-report/vehicle/utilisation/excel-file`,
    findStopTimesReports: `${REPORT}/v1/gps-report/find-stop-times-reports`,
    findStopTimesReportsWithPagination: `${REPORT}/v1/gps-report/find-stop-times-reports-paginated`,
    getJobMasterExcel: `${REPORT}/v1/job-master/download-excel-file`,
    fetchHighestRevenuedCustomers: `${REPORT}/v1/job-master/highest-revenued-customers`,
    getAllJobSummary: formatter(`${REPORT}/v1/job-summary/{0}`),
    getJobSummaryByStatus: formatter(`${REPORT}/v1/job-summary/{0}/status/{1}`),
    getLoadStatusesByLoadIds: formatter(
      `${REPORT}/v1/job-master/{0}/loads/status`,
    ),
    getLoadPreTerminationStatusesByLoadIds: formatter(
      `${REPORT}/v1/job-master/{0}/loads/pre-termination-status`,
    ),
  },
  apiManagement: {
    apis: `${ORGANIZATION}/apis`,
    apisById: formatter(`${ORGANIZATION}/apis/{0}`),
    resources: `${ORGANIZATION}/resources`,
    resourceById: formatter(`${ORGANIZATION}/resources/{0}`),
    resourcesByApi: formatter(`${ORGANIZATION}/resources/belongsToApi/{0}`),
    scopes: `${ORGANIZATION}/scopes`,
    scopeById: formatter(`${ORGANIZATION}/scopes/{0}`),
    findByBelongToApi: formatter(`${ORGANIZATION}/scopes/belongsToApi/{0}`),
    findScopesBySearch: formatter(`${ORGANIZATION}/scopes/search/{0}`),
  },
  apiGateway: {
    health: `${API_GATEWAY}/actuator/health`,
    restart: `${API_GATEWAY}/actuator/restart`,
  },
  notification: {
    smsFindByType: formatter(`${NOTIFICATION}/sms-logs/find-by-type`),
    smsLogs: formatter(`${NOTIFICATION}/sms-logs`),
    smsLogsByFilter: formatter(`${NOTIFICATION}/sms-logs/count-by-date`),
  },
  dashboard: {
    saveDashboardRows: formatter(
      `${ZUUL}/api/dashboard/org/{0}/user/{1}/save-dashboard-rows`,
    ),
  },
  serviceRequests: {
    getIncomingServiceRequests: `${SERVICE_REQUESTS}/incoming`,
    getOutgoingServiceRequests: `${SERVICE_REQUESTS}/outgoing`,
    getOutgoingTaskFeedTabCountPending: `${SERVICE_REQUESTS}/outgoing/tab-count/pending`,
    getIncomingTaskFeedTabCountActive: `${SERVICE_REQUESTS}/incoming/tab-count/active`,
    getInternallyAssignedJobs: `${SERVICE_REQUESTS}/internally-assigned`,
    getServiceRequestLoadSummaries: formatter(`${SERVICE_REQUESTS}/{0}/loads`),
    getOutgoingServiceRequestLoadDetails: formatter(
      `${SERVICE_REQUESTS}/outgoing/{0}/loads`,
    ),
    postOutgoingLoadFeedStatuses: `${SERVICE_REQUESTS}/outgoing/loads/task-feed-statuses`,
    getInternallyAssignedJobLoadDetails: formatter(
      `${SERVICE_REQUESTS}/internally-assigned/{0}/loads`,
    ),
    getServiceRequestByLoadId: formatter(`${SERVICE_REQUESTS}/by-load/{0}`),
    getServiceRequestById: formatter(`${SERVICE_REQUESTS}/{0}`),
    getScheduleOwnerTenantByJobId: formatter(
      `${SERVICE_REQUESTS}/jobs/{0}/schedule-owner-tenant`,
    ),
    getScheduleOwnerInfo: formatter(
      `${SERVICE_REQUESTS}/jobs/{0}/schedule-owner-info`,
    ),
    rejectServiceRequestCompletelyById: formatter(
      `${SERVICE_REQUESTS}/{0}/reject-all`,
    ),
    createServiceRequest: `${JOB_FILE}/service-requests`,
    createMessageThread: formatter(
      `${SERVICE_REQUESTS}/{0}/loads/{1}/message-threads`,
    ),
    getAllMessageThreads: formatter(
      `${SERVICE_REQUESTS}/{0}/loads/{1}/message-threads`,
    ),
    getMessageThreadById: formatter(
      `${SERVICE_REQUESTS}/{0}/loads/{1}/message-threads/{2}`,
    ),
    updateMessageThread: formatter(
      `${SERVICE_REQUESTS}/{0}/loads/{1}/message-threads/{2}`,
    ),
    deleteMessageThread: formatter(
      `${SERVICE_REQUESTS}/{0}/loads/{1}/message-threads/{2}`,
    ),
    // Message endpoints (ms-notifications service - basePath: /api/v1/notifications/service-requests)
    createMessage: formatter(
      `${NOTIFICATION}/service-requests/{0}/loads/{1}/message-threads/{2}/messages`,
    ),
    createMessagesBatch: formatter(
      `${NOTIFICATION}/service-requests/{0}/loads/{1}/message-threads/{2}/messages/batch`,
    ),
    getMessages: formatter(
      `${NOTIFICATION}/service-requests/{0}/loads/{1}/message-threads/{2}/messages`,
    ),
    getMessage: formatter(
      `${NOTIFICATION}/service-requests/{0}/loads/{1}/message-threads/{2}/messages/{3}`,
    ),
    updateMessage: formatter(
      `${NOTIFICATION}/service-requests/{0}/loads/{1}/message-threads/{2}/messages/{3}`,
    ),
    deleteMessage: formatter(
      `${NOTIFICATION}/service-requests/{0}/loads/{1}/message-threads/{2}/messages/{3}`,
    ),
    getMessageCount: formatter(
      `${NOTIFICATION}/service-requests/{0}/loads/{1}/message-threads/{2}/messages/count`,
    ),
    getAllMessages: `${NOTIFICATION}/service-requests/messages`,
    calculateBulkByRateCardIds: `${RATE_CARD}/rate-card/calculate-bulk-by-ratecard-ids`,
    updateMessageStatus: formatter(
      `${NOTIFICATION}/service-requests/{0}/loads/{1}/message-threads/{2}/messages/status`,
    ),
  },
  spotRFQs: {
    createSpotRFQ: SPOT_RFQS,
    getIncomingSpotRFQ: `${SPOT_RFQS}/incoming`,
    getOutgoingTaskFeedTabCountPendingOrBidsReceived: `${SPOT_RFQS}/outgoing/tab-count/pending-or-bids-received`,
    getIncomingTaskFeedTabCountActiveOrNegotiating: `${SPOT_RFQS}/incoming/tab-count/active-or-negotiating`,
    getSpotRFQLoadSummaries: formatter(`${SPOT_RFQS}/{0}/loads`),
    getSpotRFQDetailsByRFQId: formatter(`${SPOT_RFQS}/{0}`),
    publishBids: `${SPOT_RFQS}/publish-bids`,
    getOutgoingSpotRFQ: `${SPOT_RFQS}/outgoing`,
    getOutgoingSpotRFQLoadsWithBids: formatter(
      `${SPOT_RFQS}/{0}/loads-with-bids`,
    ),
    getMyBidsForSpotRFQ: formatter(`${SPOT_RFQS}/{0}/my-bids`),
    rejectSpotRFQCompletelyById: formatter(`${SPOT_RFQS}/{0}/reject`),
    updateSpotRFQ: formatter(`${SPOT_RFQS}/{0}`),
    updateBidStatus: formatter(
      `${SPOT_RFQS}/{0}/loads/{1}/suppliers/{2}/bids/{3}/bid-status`,
    ),
    // RFQ Message Thread endpoints (ms-jobfile service)
    createRfqMessageThread: formatter(
      `${SPOT_RFQS}/{0}/loads/{1}/bids/{2}/message-threads`,
    ),
    getAllRfqMessageThreads: formatter(
      `${SPOT_RFQS}/{0}/loads/{1}/bids/{2}/message-threads`,
    ),
    getRfqMessageThreadById: formatter(
      `${SPOT_RFQS}/{0}/loads/{1}/bids/{2}/message-threads/{3}`,
    ),
    updateRfqMessageThread: formatter(
      `${SPOT_RFQS}/{0}/loads/{1}/bids/{2}/message-threads/{3}`,
    ),
    addBidUpdateToThread: formatter(
      `${SPOT_RFQS}/{0}/loads/{1}/bids/{2}/message-threads/{3}/bid-updates`,
    ),
    deleteRfqMessageThread: formatter(
      `${SPOT_RFQS}/{0}/loads/{1}/bids/{2}/message-threads/{3}`,
    ),
    // RFQ Message endpoints (ms-notifications service - basePath: /api/v1/notifications/rfqs)
    createRfqMessage: formatter(
      `${NOTIFICATION}/rfqs/{0}/loads/{1}/bids/{2}/message-threads/{3}/messages`,
    ),
    createRfqMessagesBatch: formatter(
      `${NOTIFICATION}/rfqs/{0}/loads/{1}/bids/{2}/message-threads/{3}/messages/batch`,
    ),
    getRfqMessages: formatter(
      `${NOTIFICATION}/rfqs/{0}/loads/{1}/bids/{2}/message-threads/{3}/messages`,
    ),
    getRfqMessage: formatter(
      `${NOTIFICATION}/rfqs/{0}/loads/{1}/bids/{2}/message-threads/{3}/messages/{4}`,
    ),
    updateRfqMessage: formatter(
      `${NOTIFICATION}/rfqs/{0}/loads/{1}/bids/{2}/message-threads/{3}/messages/{4}`,
    ),
    deleteRfqMessage: formatter(
      `${NOTIFICATION}/rfqs/{0}/loads/{1}/bids/{2}/message-threads/{3}/messages/{4}`,
    ),
    getRfqMessageCount: formatter(
      `${NOTIFICATION}/rfqs/{0}/loads/{1}/bids/{2}/message-threads/{3}/messages/count`,
    ),
    getAllRfqMessages: `${NOTIFICATION}/rfqs/messages`,
    updateRfqMessageStatus: formatter(
      `${NOTIFICATION}/rfqs/{0}/loads/{1}/bids/{2}/message-threads/{3}/messages/status`,
    ),
  },
  agents: {
    planning: {
      chat: `${PLANNING_AGENT}/chat`,
      chatStream: `${PLANNING_AGENT}/chat/stream`,
      listThreads: `${PLANNING_AGENT}/threads/list`,
      getThread: formatter(`${PLANNING_AGENT}/threads/{0}`),
      deleteThread: formatter(`${PLANNING_AGENT}/threads/{0}`),
      registerUpload: formatter(`${PLANNING_AGENT}/threads/{0}/uploads`),
      getUploads: formatter(`${PLANNING_AGENT}/threads/{0}/uploads`),
      pinThread: formatter(`${PLANNING_AGENT}/threads/{0}/pin`),
    },
    controlTowerBrain: {
      chat: `${CONTROL_TOWER_BRAIN}/chat`,
      getThread: formatter(`${CONTROL_TOWER_BRAIN}/threads/{0}`),
      notifications: formatter(`${CONTROL_TOWER_BRAIN}/notifications?jobRef={0}`),
    },
    admin: {
      llmModels: `${CORTEX}/admin/llm/models`,
      activateModel: formatter(`${CORTEX}/admin/llm/models/{0}/activate`),
      threadCosts: `${CORTEX}/admin/llm/usage/thread-costs`,
    },
  },
  cortex: {
    textCompletion: `${CORTEX}/text-completion`,
    textCompletionTasks: `${CORTEX}/text-completion/tasks`,
  },
  auditLogs: {
    searchEvents: `${AUDIT_LOGGING}/audit-events`,
    getEvent: formatter(`${AUDIT_LOGGING}/audit-events/{0}`),
    getTenants: `${AUDIT_LOGGING}/audit-events/tenants`,
    getSourceServices: `${AUDIT_LOGGING}/audit-events/source-services`,
    hiddenRanges: `${AUDIT_LOGGING}/audit-events/hidden-ranges`,
    unhideRange: formatter(`${AUDIT_LOGGING}/audit-events/hidden-ranges/{0}/unhide`),
  },
};
